import {
  type ActiveCohortBuilderItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderModalLegacy,
  DerivedEventTypeLegacy,
  IndexDateTypeLegacy,
  SimpleEventTypeLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
  SubjectVariableTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { getRequiredDataFromState } from './getRequiredDataFromState';
import { getCohortDefinitionQueryForEditModal } from '../../adapters/getCohortDefinitionQueryForEditModal';

export const getCohortDSLValueForModalType = (
  modal: CohortBuilderModalLegacy,
  cohortDefinition: CohortBuilderDefinitionLegacy,
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy
): string => {
  switch (modal) {
    /** Study Period - This modal deviates from the other cohort builder modals
     * as it does not map to active cohort builder items
     * and does not require plugging into the runtime to generate options
     * so it is handled separately in local state
     */
    case CohortBuilderModalLegacy.CONFIGURE_STUDY_PERIOD: {
      return '';
    }

    /** Subject Variables */
    case CohortBuilderModalLegacy.ADD_SUBJECT_VARIABLE:
    case CohortBuilderModalLegacy.EDIT_SUBJECT_VARIABLE: {
      const {
        currentCohortDSL,
        id,
        name,
        type,
        conceptFilters,
        numericFilters,
        countFilter,
        currentSubjectVariable,
      } = getRequiredDataFromState(cohortDefinition, nextActiveCohortBuilderItem, modal);

      return getCohortDefinitionQueryForEditModal(currentCohortDSL, {
        newSubjectsVariable: {
          id,
          name,
          type: type as SubjectVariableTypeLegacy,
          conceptFilters,
          numericFilters,
          countFilter,
          criteriaSelection:
            currentSubjectVariable?.criteriaSelection ??
            SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED,
        },
      });
    }

    /** Index Date */
    case CohortBuilderModalLegacy.ADD_INDEX_DATE:
    case CohortBuilderModalLegacy.EDIT_INDEX_DATE: {
      const { currentCohortDSL, id, type, indexDateValue } = getRequiredDataFromState(
        cohortDefinition,
        nextActiveCohortBuilderItem,
        modal
      );

      return getCohortDefinitionQueryForEditModal(currentCohortDSL, {
        newIndexDateVariable: {
          id,
          type: type as IndexDateTypeLegacy,
          indexDateValue,
        },
      });
    }

    /** Derived Events */
    case CohortBuilderModalLegacy.ADD_DERIVED_EVENT:
    case CohortBuilderModalLegacy.EDIT_DERIVED_EVENT: {
      const {
        currentCohortDSL,
        id,
        name,
        type,
        eventFilters,
        temporalConstraints,
        uniquenessFilters,
        anyOrder,
      } = getRequiredDataFromState(cohortDefinition, nextActiveCohortBuilderItem, modal);

      return getCohortDefinitionQueryForEditModal(currentCohortDSL, {
        newDerivedEvent: {
          id,
          name,
          type: type as DerivedEventTypeLegacy,
          anyOrder,
          eventFilters,
          temporalConstraints,
          uniquenessFilters,
        },
      });
    }

    /** Simple Events */
    case CohortBuilderModalLegacy.ADD_SIMPLE_EVENT:
    case CohortBuilderModalLegacy.EDIT_SIMPLE_EVENT: {
      const {
        currentCohortDSL,
        id,
        name,
        type,
        conceptFilters,
        numericFilters,
        relatedEntityFilters,
        uniquenessFilters,
      } = getRequiredDataFromState(cohortDefinition, nextActiveCohortBuilderItem, modal);

      return getCohortDefinitionQueryForEditModal(currentCohortDSL, {
        newSimpleEvent: {
          id,
          name,
          type: type as SimpleEventTypeLegacy,
          conceptFilters,
          numericFilters,
          relatedEntityFilters,
          uniquenessFilters,
        },
      });
    }
  }
};
